/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { Box } from "@nordcloud/gnui";
import { Maybe, useCostResourceEnvironmentsQuery } from "~/generated/graphql";
import { ErrorMessage, LoaderWrap, Pagination, ReactTable } from "~/components";
import { useCurrency, useQueryState } from "~/hooks";
import { useCustomers } from "~/services/customers";
import { formatMoney, isCostResource } from "~/tools";
import {
  CostResourceSplitPerEnvironmentsList,
  MappedEnvironment,
} from "./CostResourceSplitPerEnvironmentsList";

type Environment = {
  cost?: Maybe<{
    currentMonth: string;
    previousMonth: string;
    forecast?: Maybe<string>;
  }>;
  environment?: Maybe<{
    id: string;
    name: string;
    application: {
      id: string;
      name: string;
    };
  }>;
};

function mapEnvironments(
  environments: Environment[],
  currency: string
): MappedEnvironment[] {
  return environments.map((envs) => {
    return {
      currentMonth: formatMoney(envs?.cost?.currentMonth, currency) ?? "",
      forecast: formatMoney(envs?.cost?.forecast, currency) ?? "",
      previousMonth: formatMoney(envs?.cost?.previousMonth, currency) ?? "",
      name: envs?.environment?.name ?? "",
      environment: envs.environment ?? {
        id: "",
        name: "",
        application: {
          id: "",
          name: "",
        },
      },
    };
  });
}

type Props = {
  estateRecordId: string;
  showCostResourceSplit: boolean;
};

export function CostResourceSplitPerEnvironments({
  estateRecordId,
  showCostResourceSplit,
}: Props) {
  const { currency } = useCurrency();
  const { isAdminLite } = useCustomers();
  const {
    state: { page, limit },
  } = useQueryState();

  const { data, loading, error } = useCostResourceEnvironmentsQuery({
    variables: { id: estateRecordId, limit, page },
    skip: isAdminLite,
  });

  if (data?.estateRecord == null) {
    return null;
  }

  const estateRecord = isCostResource(data?.estateRecord)
    ? data?.estateRecord
    : null;

  const count = estateRecord?.environmentsPaginated?.count ?? 0;
  const environments = estateRecord?.environmentsPaginated?.environments ?? [];

  const mappedEnvironments = mapEnvironments(environments, currency);

  return (
    <When condition={showCostResourceSplit}>
      <Box>
        <LoaderWrap loading={loading}>
          <ErrorMessage error={error} />
          <ReactTable
            columns={CostResourceSplitPerEnvironmentsList}
            data={mappedEnvironments}
          />
          <Pagination count={count} />
        </LoaderWrap>
      </Box>
    </When>
  );
}
